import { useEffect, useId, useRef, useState } from 'react'
import type { RefObject } from 'react'
import {
  CheckOutlined,
  DownloadOutlined,
  FileTextOutlined,
  IconCheck,
  IconClock,
  IconLink,
  IconMapPin,
  IconPaperPlane,
  MailOutlined,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { pluralize } from '../../lib/format/pluralize'
import { formatMoney } from '../cart/cart-money'
import { formatShipToAddressLine } from '../checkout/checkout-domain'
import type { Rfq } from './domain'
import { rfqIdSuffix } from './domain'

type AddressView = components['schemas']['AddressView']

/** Display-only stub carriers/methods, same lists as the cart's own
 * `GenerateQuoteModal` (`dc.html:4978/4980`). ADR-0143 D4: unlike the cart's,
 * the METHOD select is SEEDED from the RFQ's persisted `shipMethod` — the RFQ
 * shipping estimate below is derived from that method server-side (ADR-0125),
 * so defaulting to "Priority Overnight" would visibly contradict the
 * "(ground)" qualifier one block down. Neither select leaves the browser: no
 * RFQ share/quote request carries a carrier/method field. */
const SHIP_COMPANIES = ['FedEx', 'UPS', 'DHL Express', 'USPS'] as const
const SHIP_METHODS = ['Priority Overnight', '2 Day', 'Ground', 'Economy'] as const

/** Gate-5 W4 — `own_account` and "no method saved" have NO honest counterpart
 * among the carrier tiers above. Seeding the select to "Priority Overnight" in
 * those cases contradicted this modal's OWN shipping row two blocks below
 * ("Billed by carrier" / "No shipping method selected yet") on one screen —
 * the exact self-contradiction class ADR-0125 W1 exists to remove. These
 * placeholder values say what is true instead. */
const SHIP_METHOD_OWN_ACCOUNT = 'Not applicable — billed by carrier'
const SHIP_METHOD_NONE = 'None selected'

/** ADR-0143 D4 — `RfqShipMethod` → the display-only method option that does not
 * contradict it. `own_account` has NO honest counterpart in the stub list (the
 * customer's own carrier account is not one of these tiers, and the shipping row
 * renders "Billed by carrier" rather than a figure), so it falls through to the
 * first option AND trips {@link methodDivergesFromRfq} — which is the correct
 * outcome: the caption then says the estimate follows the RFQ's saved method. */
function seededShipMethod(shipMethod: Rfq['shipMethod']): string {
  if (shipMethod === 'overnight') return 'Priority Overnight'
  if (shipMethod === 'ground') return 'Ground'
  if (shipMethod === 'own_account') return SHIP_METHOD_OWN_ACCOUNT
  return SHIP_METHOD_NONE
}

/** The option list for a given RFQ: the four carrier tiers, plus the honest
 * placeholder as the SELECTED first entry when the RFQ's own method has no
 * tier equivalent. */
function shipMethodOptions(shipMethod: Rfq['shipMethod']): readonly string[] {
  const seeded = seededShipMethod(shipMethod)
  return SHIP_METHODS.includes(seeded as (typeof SHIP_METHODS)[number])
    ? SHIP_METHODS
    : [seeded, ...SHIP_METHODS]
}

/** Gate-5 W4 — the disclosure is CASE-AWARE. The old single sentence asserted
 * "the RFQ's saved shipping method" even when none is saved, which is simply
 * false for the null case. Returns null when the selection still corresponds
 * to the RFQ, so the common (seeded) path carries no extra chrome. */
function shipMethodNote(selected: string, shipMethod: Rfq['shipMethod']): string | null {
  if (shipMethod === 'own_account') {
    return 'Shipping on this RFQ is billed to your own carrier account, so these selections appear on the quotation only — they do not re-price it.'
  }
  if (shipMethod !== 'overnight' && shipMethod !== 'ground') {
    return 'This RFQ has no saved shipping method, so these selections appear on the quotation only — they do not re-price it.'
  }
  if (selected !== seededShipMethod(shipMethod)) {
    return 'This estimate reflects the RFQ’s saved shipping method — changing these does not re-price it.'
  }
  return null
}

/** Display-only "recently sent to" stub suggestions, mirroring the cart modal's
 * (`dc.html:4062-4070`) — no send endpoint exists; see {@link EMAIL_HELPER}. */
const RECENT_RECIPIENTS: ReadonlyArray<{ readonly name: string; readonly email: string }> = [
  { name: 'Maya Chen', email: 'maya.chen@northwindrobotics.com' },
  { name: 'Sam Rivera', email: 'sam.rivera@northwindrobotics.com' },
]

const EMAIL_HELPER = "We'll send a copy of this quote to that address."

/** How long "Copy shareable link" holds its copied checkmark before reverting. */
const COPIED_RESET_MS = 2000

/** ADR-0143 D1 — a floor on how long the scanning-document animation stays
 * visible, regardless of how fast `POST /rfq/{rfqId}/mark-ready` resolves. It
 * does NOT fabricate a result: the real request runs concurrently and its
 * outcome is unaffected; this only delays the state transition so the animation
 * is perceivable instead of flashing for one frame on a fast connection. Same
 * value and rationale as the cart modal's own floor. */
/** Kept in step with `cart/GenerateQuoteModal.tsx`'s exported
 * `MIN_GENERATING_MS`, which is the SAME animation spec rendered twice. NOT
 * imported from there on purpose: that would pull the whole cart modal module
 * into this vertical's chunk for a timing literal, and ADR-0143 D2 keeps the
 * two components independent. If one is tuned, tune both — this comment is the
 * grep anchor that says so. */
const MIN_GENERATING_MS = 1000

/** What one money row's value should render, plus whether it is a real figure
 * (money styling) or an explanatory note (`caption-text`). Structurally the
 * `MoneyRowContent` the rail's `shippingRowContent`/`taxRowContent` already
 * return — ADR-0143 D3 passes those results straight through rather than
 * re-deriving them, so ADR-0125 W1/W3's honest fallbacks ("Billed by carrier",
 * "No ship-to address selected", …) survive into this modal verbatim and the
 * never-`0.00`/never-`—` constraint holds for free. */
export interface RfqShareQuoteMoneyRow {
  readonly content: string
  readonly isFigure: boolean
}

export interface RfqShareQuoteModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly rfq: Rfq
  /** The rail's OWN variant-/quantity-adjusted part-production figure — never a
   * re-derivation (ADR-0143 D3 / ADR-0131). `undefined` when the RFQ has no
   * priced lines to total. */
  readonly partProduction: { readonly amount: string; readonly currency: string } | undefined
  readonly shippingRow: RfqShareQuoteMoneyRow & { readonly qualifier: string | null }
  readonly taxRow: RfqShareQuoteMoneyRow
  /** The rail's already-footing summed total (part production + shipping + tax). */
  readonly quoteTotal: { readonly amount: string; readonly currency: string } | undefined
  readonly partCount: number
  /** The rail's resolved lead-time label, rendered as the ready card's
   * "arrives …" (`dc.html:5020`). */
  readonly arrivalLabel: string
  /** `POST /rfq/{rfqId}/mark-ready` via `useShareHandoff.markReady`. Resolves
   * true on success. ADR-0143 D6: idempotent for a live session and minting for
   * an expired one, which is what lets all three handoff sub-states share this
   * one path. */
  readonly onGenerate: () => Promise<boolean>
  /** The hook's `markError`, rendered as the confirm state's inline alert. */
  readonly generateError: string | undefined
}

type ViewState = 'confirm' | 'generating' | 'ready' | 'error'

/**
 * The RFQ detail screen's share-quote modal (UID-438 / ADR-0143 D1) — the
 * design's RFQ-variant of the three-state quote modal (`dc.html:4962-5075`,
 * RFQ copy selected by `qm.rfq` at `:9158-9159`, opened in place by
 * `openRfqQuote` at `:7988` from `onShare` at `:9775`):
 *
 *  - **confirm**: "Share quote" + the RFQ subtitle, the resolved ship-to,
 *    display-only carrier/method selects, and the rail's own quote summary.
 *  - **generating**: the scanning-document animation while
 *    `POST /rfq/{rfqId}/mark-ready` is in flight.
 *  - **ready**: "Quote generated", the quote-number card, a display-only
 *    "email this quote" stub, Download PDF / Copy shareable link, and the
 *    single full-width **Done** the `isReadyRfq` footer specifies (`:5065`).
 *
 * A failed generate returns to **confirm** with an inline alert on top, so the
 * user's picks survive a retry.
 *
 * REUSES the cart modal's `.generate-quote-*` stylesheet verbatim
 * (`cart.css:2090-2660`, global via `global.css:7`) — **this ticket adds no
 * CSS**. `cart/GenerateQuoteModal.tsx` is deliberately NOT shared or refactored:
 * its props require `cart: CartView` and it POSTs `/checkout/quote`, neither of
 * which exists here, and touching it would put its three shipped call sites
 * (CartPage, OrderSummaryRail, CheckoutSummaryRail) at risk to fix a defect on a
 * fourth surface. The `QuoteModalShell` extraction that would DRY the two is
 * recorded as the rejected alternative in ADR-0143 D2, not forgotten.
 */
export function RfqShareQuoteModal({
  open,
  onClose,
  rfq,
  partProduction,
  shippingRow,
  taxRow,
  quoteTotal,
  partCount,
  arrivalLabel,
  onGenerate,
  generateError,
}: RfqShareQuoteModalProps) {
  const { showToast } = useToast()
  const [viewState, setViewState] = useState<ViewState>('confirm')
  const [shipCompany, setShipCompany] = useState<string>(SHIP_COMPANIES[0])
  const [shipMethod, setShipMethod] = useState<string>(seededShipMethod(rfq.shipMethod))
  const [address, setAddress] = useState<AddressView | undefined>(undefined)
  const [email, setEmail] = useState('')
  const [emailMenuOpen, setEmailMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  /** Gate-5 Critical — the message shown when `onGenerate` REJECTS instead of
   * resolving false. `useShareHandoff.markReady` is `try/finally` with no
   * `catch`, so on a thrown fetch error it never sets `markError`, and the
   * `generateError` prop stays undefined; without this the error state would
   * render an empty alert. */
  const [thrownError, setThrownError] = useState<string | undefined>(undefined)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  /** Gate-5 — `Modal`'s focus effect is keyed on `[open, initialFocusRef]`, and
   * `open` does not change when this component swaps between its three states,
   * so without a DISTINCT ref per state the previously-focused node unmounts and
   * focus falls to `<body>` (outside the dialog, behind the backdrop). Handing
   * `Modal` a different ref object per state re-runs that effect on every
   * transition. The generating state especially needs one: it renders no button
   * at all, so `Modal`'s Tab trap finds zero focusable nodes and early-returns. */
  const generatingFocusRef = useRef<HTMLElement | null>(null)
  const readyFocusRef = useRef<HTMLElement | null>(null)
  const emailMenuWrapRef = useRef<HTMLDivElement | null>(null)
  const shipCompanyId = useId()
  const shipMethodId = useId()
  const emailId = useId()
  const emailNoteId = useId()
  const methodNoteId = useId()

  // Reset to a clean confirm state every time the modal is (re-)opened, and
  // re-seed the selects from the RFQ — a previous generate/error run must never
  // bleed into the next open. Keyed on `open` only: the ship-to and ship method
  // are properties of an already-quoted RFQ, not something edited from in here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    if (!open) return
    setViewState('confirm')
    setShipCompany(SHIP_COMPANIES[0])
    setShipMethod(seededShipMethod(rfq.shipMethod))
    setEmail('')
    setEmailMenuOpen(false)
    setCopied(false)
    setThrownError(undefined)
  }, [open])

  // The ship-to card's content. The RFQ detail page does not load the address
  // book, so this modal fetches it the same way the cart modal fetches its own
  // confirm-state data on open. A failure is deliberately NOT a blocker: the
  // ship-to line is context, and the quote being shared is already priced — so
  // the card degrades to its label-less fallback and "Generate quote" stays
  // enabled, rather than gating a share on a cosmetic read.
  useEffect(() => {
    if (!open || !rfq.shipToAddressId) return
    let cancelled = false
    async function load() {
      const { data } = await apiClient.GET('/account/addresses')
      if (cancelled || !data) return
      setAddress(data.addresses.find((candidate) => candidate.id === rfq.shipToAddressId))
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, rfq.shipToAddressId])

  // A pending "Copied!" revert must never fire after this component is gone.
  useEffect(() => {
    return () => clearTimeout(copiedTimeoutRef.current)
  }, [])

  // Dismiss the "Recently sent to" menu on an outside pointer-down. Bound only
  // while the menu is open, so there is no idle document listener. Same
  // treatment (and same reason) as the cart modal's, whose menu had no
  // dismissal path at all until UID-403.
  useEffect(() => {
    if (!emailMenuOpen) return
    function handlePointerDown(event: PointerEvent) {
      const wrap = emailMenuWrapRef.current
      if (wrap && event.target instanceof Node && !wrap.contains(event.target)) {
        setEmailMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [emailMenuOpen])

  async function handleGenerate() {
    setViewState('generating')
    setThrownError(undefined)
    const minDelay = new Promise((resolve) => setTimeout(resolve, MIN_GENERATING_MS))
    // Gate-5 Critical — the `catch` is LOAD-BEARING, not defensive boilerplate.
    // `openapi-fetch` rethrows when `fetch` itself rejects and no middleware
    // handles it (`openapi-fetch/dist/index.js:198-200`; `lib/api/client.ts`
    // registers no `onError`), and `markReady` is `try/finally` with no
    // `catch` — so a dropped connection propagates here. Without this, the
    // `setViewState` below never ran and the modal was stranded on the
    // generating state forever: `hideHeader`, no footer, ZERO focusable
    // elements, plus an unhandled rejection. `cart/GenerateQuoteModal.tsx`
    // (:254-276) has this exact guard; it was lost in the port.
    try {
      const [succeeded] = await Promise.all([onGenerate(), minDelay])
      setViewState(succeeded ? 'ready' : 'error')
    } catch {
      // Still honour the animation floor, so the failure does not flash past.
      await minDelay
      setThrownError('Something went wrong. Please try again.')
      setViewState('error')
    }
  }

  /** UID-456 — copies an absolute INTERNAL link to the RFQ detail page
   * (`${window.location.origin}/rfq/{rfq.id}`), mirroring
   * `cart/GenerateQuoteModal.tsx`'s `handleCopyLink` exactly. Replaces the
   * previous approach of copying `session.shareUrl` (an external, no-login
   * share-session link that fell back to a non-functional display string,
   * `…/s/{shareToken}`, whenever `shareUrl` was null — see the removed
   * `shareLinkDisplay`). `rfq.id` is always present, so this never has a
   * null/fallback case to get wrong. */
  async function handleCopyLink() {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable')
      }
      const absoluteUrl = `${window.location.origin}/rfq/${rfq.id}`
      await navigator.clipboard.writeText(absoluteUrl)
      setCopied(true)
      clearTimeout(copiedTimeoutRef.current)
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
    } catch {
      // Clipboard permission denied/unavailable/thrown — no crash. Same wording as
      // `cart/GenerateQuoteModal.tsx`'s `handleCopyLink`: the copy target is a real,
      // useful URL, so "select the link and copy it manually" is accurate advice again
      // (unlike the previous session-link version, which had no link visible on screen).
      showToast(
        "Couldn't copy the link automatically — select the link and copy it manually.",
        'error',
      )
    }
  }

  /** Emailing the quote is a deliberate demo simulation (no send endpoint on the
   * contract) — a real, enabled action firing a success toast, the same honest
   * pattern the cart modal and "Send to PunchOut" already use. */
  function handleSendQuote() {
    const to = email.trim()
    if (!to) {
      showToast('Enter an email address to send this quote to.', 'info')
      return
    }
    showToast(`Quote sent to ${to}`)
    setEmail('')
    setEmailMenuOpen(false)
  }

  /** Demo simulation — no PDF-generation endpoint exists. ADR-0143 records this
   * as an open follow-up: this flow mints a share session, not a quotation
   * document. */
  function handleDownloadPdf() {
    showToast('Quote PDF downloaded')
  }

  if (viewState === 'generating') {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Generating your quote…"
        maxWidth={540}
        hideHeader
        className="generate-quote-modal-generating"
        initialFocusRef={generatingFocusRef}
      >
        {/* The design's scanning-document animation (`dc.html:4996-5010`).
            Purely decorative — `prefers-reduced-motion` neutralizes it in
            cart.css, and the heading/detail below is the accessible status.
            `<output>` carries an implicit `role="status"`. */}
        {/* `tabIndex={-1}` + `initialFocusRef` so this state has a focus
            target at all — see `generatingFocusRef`'s doc comment. */}
        <output
          className="generate-quote-generating"
          ref={generatingFocusRef as RefObject<HTMLOutputElement>}
          tabIndex={-1}
        >
          <div className="qg-doc" aria-hidden="true">
            <div className="qg-scan" />
            <div className="qg-line" style={{ top: 16, width: 52, animationDelay: '0s' }} />
            <div className="qg-line" style={{ top: 30, width: 40, animationDelay: '.12s' }} />
            <div className="qg-line" style={{ top: 44, width: 52, animationDelay: '.24s' }} />
            <div className="qg-line" style={{ top: 58, width: 34, animationDelay: '.36s' }} />
            <div
              className="qg-line"
              style={{
                top: 76,
                width: 46,
                height: 9,
                background: 'var(--color-brand-tint-100)',
                animationDelay: '.48s',
              }}
            />
          </div>
          <p className="generate-quote-generating-heading">Generating your quote…</p>
          <p className="generate-quote-generating-detail">
            Pricing {partCount} {pluralize(partCount, 'part')} and locking in your lead times
          </p>
          <div className="generate-quote-generating-progress" aria-hidden="true">
            <div className="generate-quote-generating-progress-fill" />
          </div>
        </output>
      </Modal>
    )
  }

  if (viewState === 'ready') {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Quote generated"
        maxWidth={540}
        // The design's ready state has NO header bar — the body draws its own
        // centered check + heading (`dc.html:5011-5017`).
        hideHeader
        // Scopes `.modal-body { overflow: visible }` to this state so the
        // "Recently sent to" menu can float past the body's box (`:5017`).
        className="generate-quote-modal-ready"
        initialFocusRef={readyFocusRef}
        footer={
          // `dc.html:5065-5069` (`isReadyRfq`) — ONE full-width Done, not the
          // cart variant's "Continue shopping"/"Proceed to checkout" pair.
          <Button variant="primary" size="xl" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div
          className="generate-quote-ready"
          ref={readyFocusRef as RefObject<HTMLDivElement>}
          tabIndex={-1}
        >
          <span className="generate-quote-ready-icon" aria-hidden="true">
            <IconCheck />
          </span>
          <p className="generate-quote-ready-heading">Quote generated</p>
          {/* `dc.html:9159` — `readySub` under `qm.rfq`, which is the RFQ's own
              copy, NOT the cart's "emailed to {email}" line. */}
          <p className="generate-quote-ready-detail">
            Your quotation PDF is ready to download or share.
          </p>
        </div>
        <div className="generate-quote-card">
          <div className="generate-quote-card-head">
            <div>
              <p className="generate-quote-card-eyebrow">Quote number</p>
              {/* The RFQ's own human reference when the server sends one,
                  falling back to the id suffix `ShareHandoffModal` used for its
                  generated title (UID-54 UX spec §2.2 point 2). */}
              <p className="generate-quote-card-ref">{rfq.rfqNumber ?? rfqIdSuffix(rfq.id)}</p>
            </div>
            <div className="generate-quote-card-totals">
              <p className="caption-text">
                {partCount} {pluralize(partCount, 'part')} · arrives {arrivalLabel}
              </p>
              {quoteTotal && (
                <p className="generate-quote-card-total">
                  {formatMoney(quoteTotal.amount, quoteTotal.currency)}
                </p>
              )}
            </div>
          </div>
          <div className="generate-quote-email-row">
            <label htmlFor={emailId} className="generate-quote-eyebrow-label">
              Email this quote
            </label>
            <div className="generate-quote-email-input-row">
              {/* `onKeyDown` on the whole wrap so Escape closes just the
                  DROPDOWN and stops the event before Modal's own Escape handler
                  closes the dialog and loses the generated quote. */}
              <div
                className="generate-quote-email-input-wrap"
                ref={emailMenuWrapRef}
                onKeyDown={(event) => {
                  if (event.key === 'Escape' && emailMenuOpen) {
                    event.stopPropagation()
                    setEmailMenuOpen(false)
                  }
                }}
              >
                <span className="generate-quote-email-icon" aria-hidden="true">
                  <MailOutlined />
                </span>
                <input
                  id={emailId}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onFocus={() => setEmailMenuOpen(true)}
                  placeholder="Enter or pick an email address…"
                  aria-describedby={emailNoteId}
                  className="generate-quote-email-input"
                />
                {emailMenuOpen && (
                  <div className="generate-quote-email-menu" role="menu">
                    <p className="generate-quote-email-menu-heading">Recently sent to</p>
                    {RECENT_RECIPIENTS.map((recipient) => (
                      <button
                        key={recipient.email}
                        type="button"
                        role="menuitem"
                        className="generate-quote-email-suggestion"
                        onClick={() => {
                          setEmail(recipient.email)
                          setEmailMenuOpen(false)
                        }}
                      >
                        <span className="generate-quote-email-suggestion-name">
                          {recipient.name}
                        </span>
                        <span className="generate-quote-email-suggestion-address">
                          {recipient.email}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="secondary" aria-describedby={emailNoteId} onClick={handleSendQuote}>
                <IconPaperPlane />
                Send
              </Button>
            </div>
          </div>
          <p id={emailNoteId} className="caption-text generate-quote-email-note">
            {EMAIL_HELPER}
          </p>
        </div>

        {/* Gate-5 CRITICAL 2 — the caveat the retired `ShareHandoffModal` called
            MANDATORY, restored. It is not optional politeness: the figure above
            is THIS rail's variant- and locally-edited-quantity-adjusted CLIENT
            total, while the link's recipient opens a `SharedSessionSummary`
            whose `subtotalAmount` the server derives from the BASE variant and
            the SERVER quantity — `apps/api/src/modules/session/domain.ts`'s
            `buildSharedSessionSummary`, whose own doc says "Consumers MUST NOT
            treat it as an order total", echoed by the schema's
            "Non-authoritative preview only". Dropping this caveat while the
            confirm step asserts pricing is "locked for 30 days" told the
            engineer their number is the number the purchaser will see. It is
            not. ADR-0143 D3 secured rail-to-modal coherence and never checked
            modal-to-shared-artifact; this is that gap closed. */}
        <p className="caption-text generate-quote-email-note">
          Preview only — the purchaser sees the confirmed total when they open this.
        </p>

        <div className="generate-quote-actions-row">
          <Button variant="secondary" size="xl" onClick={handleDownloadPdf}>
            <DownloadOutlined aria-hidden="true" />
            Download quote (PDF)
          </Button>
          {/* UID-456 — copies `${window.location.origin}/rfq/{rfq.id}` (see
              `handleCopyLink`), matching cart's `GenerateQuoteModal` wording/pattern.
              `rfq.id` is always present, so — unlike the previous session-link
              version — this is never a dead button with nothing to copy. */}
          <Button variant="secondary" size="xl" onClick={() => void handleCopyLink()}>
            {copied ? <CheckOutlined aria-hidden="true" /> : <IconLink />}
            {copied ? 'Copied!' : 'Copy quote link'}
          </Button>
        </div>
      </Modal>
    )
  }

  // confirm + error share one shell — an error is an inline banner on top of an
  // otherwise UNCHANGED confirm state, so a retry keeps the user's picks.
  const methodNote = shipMethodNote(shipMethod, rfq.shipMethod)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Share quote"
      // `dc.html:9158` — the RFQ variant's own title + subtitle, in ONE header
      // block via Modal's `subtitle` slot.
      subtitle={
        <>
          Your RFQ came back fully priced — confirm the details and we&rsquo;ll generate a formal
          quotation to share.
        </>
      }
      maxWidth={540}
      className="generate-quote-modal-confirm"
      footer={
        <>
          <Button variant="secondary" size="xl" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xl"
            className="generate-quote-generate-btn"
            onClick={() => void handleGenerate()}
          >
            <FileTextOutlined aria-hidden="true" />
            Generate quote
          </Button>
        </>
      }
    >
      {/* Gate-5 — `generateError ?? thrownError`, never `generateError` alone:
       * a REJECTED `markReady` leaves the prop undefined (it has no `catch`),
       * which used to render the error state with no alert at all. */}
      {viewState === 'error' && (generateError ?? thrownError) && (
        <div role="alert" className="generate-quote-error">
          <p className="form-error-text">{generateError ?? thrownError}</p>
        </div>
      )}

      <p className="generate-quote-section-label">Ship to</p>
      <div className="generate-quote-ship-to-card">
        <span className="generate-quote-ship-to-icon" aria-hidden="true">
          <IconMapPin />
        </span>
        <div className="generate-quote-ship-to-body">
          <p className="generate-quote-ship-to-name">
            {address?.contactName ?? address?.label ?? 'Ship-to address'}
          </p>
          {address && (
            <p className="generate-quote-ship-to-line">{formatShipToAddressLine(address)}</p>
          )}
        </div>
        {/* Display-only, matching the comp's `onBack: ()=>this.closeQuote()`
            (`dc.html:9174`) and the cart modal's own "Change": the ship-to is
            edited on the RFQ, not re-picked inside a share dialog. */}
        <button type="button" className="generate-quote-ship-to-change" onClick={onClose}>
          Change
        </button>
      </div>

      <div className="generate-quote-shipping-grid">
        <div className="form-field">
          <label htmlFor={shipCompanyId}>Shipping company</label>
          <select
            id={shipCompanyId}
            value={shipCompany}
            onChange={(event) => setShipCompany(event.target.value)}
          >
            {SHIP_COMPANIES.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor={shipMethodId}>Shipping method</label>
          <select
            id={shipMethodId}
            value={shipMethod}
            aria-describedby={methodNote ? methodNoteId : undefined}
            onChange={(event) => setShipMethod(event.target.value)}
          >
            {shipMethodOptions(rfq.shipMethod).map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* ADR-0143 D4, corrected at Gate 5 — the selects are display-only; the
          shipping figure below is derived server-side from the RFQ's PERSISTED
          method. The wording is now case-aware (see `shipMethodNote`): the old
          single sentence claimed a "saved shipping method" even for an RFQ that
          has none. Wired via `aria-describedby` so it reaches a screen reader
          that never sees the caption's position. */}
      {methodNote && (
        <p id={methodNoteId} className="caption-text generate-quote-ship-method-note">
          {methodNote}
        </p>
      )}

      <p className="generate-quote-section-label">Quote summary</p>
      {/* ADR-0143 D3 — every figure here is the RAIL's, passed in as props. The
          modal deliberately cannot compute a total that disagrees with the rail
          behind it (ADR-0131), and the rail's honest non-figure fallbacks
          (ADR-0125 W1/W3) pass through verbatim rather than being re-derived. */}
      <dl className="cart-summary-totals generate-quote-summary-totals">
        <dt>
          Part production ({partCount} {pluralize(partCount, 'part')})
        </dt>
        <dd>
          {partProduction
            ? formatMoney(partProduction.amount, partProduction.currency)
            : 'Not available for this quote'}
        </dd>
        <dt>
          Shipping
          {shippingRow.qualifier && (
            <>
              {' '}
              <span className="rfq-detail-summary-qualifier">({shippingRow.qualifier})</span>
            </>
          )}
        </dt>
        <dd className={shippingRow.isFigure ? undefined : 'caption-text'}>{shippingRow.content}</dd>
        <dt>Tax</dt>
        <dd className={taxRow.isFigure ? undefined : 'caption-text'}>{taxRow.content}</dd>
        <dt className="generate-quote-total-label">Quote total</dt>
        <dd className="generate-quote-total-value">
          {quoteTotal
            ? formatMoney(quoteTotal.amount, quoteTotal.currency)
            : 'Not available for this quote'}
        </dd>
      </dl>
      <p className="generate-quote-lock-note">
        <IconClock />
        Pricing and lead times are locked into this quote for 30 days.
      </p>
    </Modal>
  )
}
